import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { themes, DEFAULT_THEME } from "../themes/themes";
import { useGlobal } from "../global/user";
import { Companies } from "../api";
import {
  BRANDING_CACHE_KEY,
  DEFAULT_BRANDING,
  DEFAULT_FAVICON,
  DEFAULT_LOGIN_IMAGE,
  DEFAULT_TITLE,
  THEME_STORAGE_KEY,
  clearCachedBranding,
  normalizeBranding,
  readCachedBranding,
  resolveAssetUrl,
  writeCachedBranding,
} from "./brandingDefaults";

// 把主題顏色餵給 antd 的 ConfigProvider，讓元件原生跟著主題走，
// 不必再用 !important 硬蓋樣式
const px = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
};

function buildAntdTheme(t) {
  const c = t.colors || {};
  const st = t.styles || {};
  return {
    algorithm: t.isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: c.antPrimaryColor,
      colorLink: c.antLinkColor || c.antPrimaryColor,
      colorText: c.primaryText,
      colorTextSecondary: c.secondaryText,
      colorBorder: c.borderColor,
      colorBorderSecondary: c.borderColor,
      colorBgContainer: c.contentBackground,
      colorBgElevated: c.modalBackground || c.contentBackground,
      borderRadius: px(st.borderRadiusMedium, 6),
      fontFamily: st.fontFamily,
      fontSize: px(st.fontSizeBase, 14),
    },
    components: {
      Layout: { headerBg: c.headerBackground, bodyBg: "transparent" },
      Table: {
        headerBg: c.tableHeaderBackground,
        headerColor: c.primaryText,
        // 表格外層：背景圖主題設成 transparent，避免與儲存格疊成兩層而發灰
        colorBgContainer: c.tableWrapperBackground || c.tableBackground,
        rowHoverBg: c.tableRowHoverBackground || c.tableRowOddBackground,
        borderColor: c.borderColor,
      },
      Button: {
        defaultBg: c.buttonBackground,
        defaultColor: c.buttonText,
        defaultBorderColor: c.buttonBorder,
      },
      Modal: {
        contentBg: c.modalBackground,
        headerBg: c.modalBackground,
        titleColor: c.primaryText,
      },
      Pagination: { itemActiveBg: c.antPrimaryColor },
      Select: { optionSelectedBg: c.tableRowOddBackground },
      Input: { colorBgContainer: c.contentBackground },
    },
  };
}

const ThemeContext = createContext();

const themeExists = (name) => Boolean(name && themes[name]);

// 讀取瀏覽器上的主題偏好（只有未鎖定主題的使用者，也就是網管，會用到）
function readStoredTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return themeExists(saved) ? saved : null;
  } catch (e) {
    return null;
  }
}

function resolveTheme(branding) {
  if (branding.theme_locked) {
    return themeExists(branding.theme) ? branding.theme : DEFAULT_THEME;
  }
  return readStoredTheme() || DEFAULT_THEME;
}

// 動態替換瀏覽器分頁圖示
function applyFavicon(href) {
  let link = document.querySelector("link[data-branding-icon]");
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("data-branding-icon", "true");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
}

export function ThemeProvider({ children }) {
  const { user } = useGlobal();

  // 先用快取的品牌，避免重新整理時閃一下預設標題／主題
  const [branding, setBranding] = useState(() => readCachedBranding() || DEFAULT_BRANDING);
  const [currentTheme, setCurrentTheme] = useState(() =>
    resolveTheme(readCachedBranding() || DEFAULT_BRANDING)
  );

  // 登入後：品牌由後端 checkToken 回傳的 userInfo.branding 決定
  useEffect(() => {
    if (!user || !user.id) return;
    const next = normalizeBranding(user.branding);
    setBranding(next);
    writeCachedBranding(next);
  }, [user]);

  // 品牌改變時決定主題：鎖定就用公司主題，未鎖定就用瀏覽器偏好
  useEffect(() => {
    setCurrentTheme(resolveTheme(branding));
  }, [branding]);

  // 套用主題（CSS 變數、主題 CSS 檔、body class）
  useEffect(() => {
    const theme = themes[currentTheme] || themes[DEFAULT_THEME];
    const root = document.documentElement;

    const existingThemeLinks = document.querySelectorAll("link[data-theme-css]");
    existingThemeLinks.forEach((link) => link.remove());

    if (theme.hasCssFile) {
      const themeCssLink = document.createElement("link");
      themeCssLink.rel = "stylesheet";
      // 加上 PUBLIC_URL：正式環境的實際路徑是 /IoT3/themes/xxx-theme.css
      themeCssLink.href = `${process.env.PUBLIC_URL || ""}/themes/${theme.name}-theme.css`;
      themeCssLink.setAttribute("data-theme-css", "true");
      document.head.appendChild(themeCssLink);
    }

    const body = document.body;
    const allThemeClasses = Object.keys(themes).map((t) => `theme-${t}`);
    body.classList.remove(...allThemeClasses);
    body.classList.add(`theme-${theme.name}`);

    // 背景圖（部分主題有）：交給 body::before 那層固定底圖使用
    root.style.setProperty(
      "--bg-image",
      theme.backgroundImage
        ? `url("${process.env.PUBLIC_URL || ""}/themes/backgrounds/${theme.backgroundImage}")`
        : "none"
    );

    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${camelToKebab(key)}`, value);
    });
    Object.entries(theme.styles).forEach(([key, value]) => {
      root.style.setProperty(`--${camelToKebab(key)}`, value);
    });

    // 主題被公司鎖定時不要寫入偏好，以免蓋掉網管在這台瀏覽器的設定
    if (!branding.theme_locked) {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, theme.name);
      } catch (e) {
        /* ignore */
      }
    }
  }, [currentTheme, branding.theme_locked]);

  // 套用標題與 favicon
  useEffect(() => {
    document.title = branding.title || DEFAULT_TITLE;
    applyFavicon(resolveAssetUrl(branding.favicon_url) || DEFAULT_FAVICON);
  }, [branding]);

  const changeTheme = (themeName) => {
    if (branding.theme_locked) return; // 由公司指定，不可切換
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  // 登入頁用：依網址代碼 #/{slug} 取得該公司的品牌
  const applyLoginBranding = useCallback(async (slug) => {
    if (!slug) {
      setBranding({ ...DEFAULT_BRANDING });
      return null;
    }
    try {
      const data = await Companies.getBrandingBySlug(slug);
      if (!data) {
        // 查無此代碼：顯示系統預設，不提示錯誤
        setBranding({ ...DEFAULT_BRANDING });
        return null;
      }
      const next = normalizeBranding({ ...data, theme_locked: true });
      setBranding(next);
      return next;
    } catch (e) {
      console.warn("取得登入頁品牌失敗", e);
      setBranding({ ...DEFAULT_BRANDING });
      return null;
    }
  }, []);

  const resetBranding = useCallback(() => {
    clearCachedBranding();
    setBranding({ ...DEFAULT_BRANDING });
  }, []);

  const availableThemes = Object.keys(themes).map((key) => ({
    name: key,
    displayName: themes[key].displayName || getThemeDisplayName(key),
  }));

  const activeTheme = themes[currentTheme] || themes[DEFAULT_THEME];
  const antdConfig = useMemo(() => buildAntdTheme(activeTheme), [activeTheme]);

  const value = {
    currentTheme: activeTheme.name,
    changeTheme,
    availableThemes,
    themeColors: activeTheme.colors,
    themeStyles: activeTheme.styles,
    // 品牌
    branding,
    isThemeLocked: branding.theme_locked,
    brandTitle: branding.title || DEFAULT_TITLE,
    brandLogoUrl: resolveAssetUrl(branding.logo_url),
    brandLoginImage: resolveAssetUrl(branding.login_image_url) || DEFAULT_LOGIN_IMAGE,
    applyLoginBranding,
    resetBranding,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// 只需要品牌資訊的元件用這個
export function useBranding() {
  const { branding, brandTitle, brandLogoUrl, brandLoginImage, applyLoginBranding, resetBranding } =
    useTheme();
  return {
    branding,
    title: brandTitle,
    logoUrl: brandLogoUrl,
    loginImage: brandLoginImage,
    slug: branding.slug,
    applyLoginBranding,
    resetBranding,
  };
}

export { BRANDING_CACHE_KEY, THEME_STORAGE_KEY };

function camelToKebab(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

function getThemeDisplayName(themeName) {
  const displayNames = {
    basic: "基本",
    dark: "深色",
    astra: "星辰",
    cute: "可愛",
    green: "清新綠",
    illustration: "插畫風格",
    baroque: "巴洛克風格",
  };
  return displayNames[themeName] || themeName;
}